import React, { useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { CustomerAccountService, useProjectConfiguration } from '../../../../services/customer-account';
import { StormDoorColor } from '../../../../entities-generated/common/storm-door-color';
import { ITheme, IStyle, mergeStyleSets, getTheme, Icon, MessageBarType } from '@fluentui/react';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { useNavigator } from '../../../../framework/services/navigation';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { BwjButton, BwjButtonType } from '../../../../framework/components/bwj-button';
import { IUpdateStormDoorColorPreferenceRequest } from '../../../../entities-generated/models/storm-door/update-storm-door-color-preference-request';
import { ColorApiClient } from '../../../../services/color-prefs';
import { getNextShoppingUrl } from '../../../app-routes';
import { BwjPalette } from '../../../../app-core/app-theme';
import { StormDoorColorSelector } from '../../../components/st-door-color-select';

const getClasses = (theme: ITheme) => {
  const main: IStyle = {
    margin: 0,
    marginTop: '2em',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: theme.palette.white,
    fontSize: theme.fonts.large.fontSize,
    fontWeight: 400,
  };

  const palette: IStyle = {
    fontSize: 100,
  };

  const title: IStyle = {
    fontSize: theme.fonts.xxLarge.fontSize,
    lineHeight: theme.fonts.xxLarge.lineHeight,
    fontWeight: 'bold',
    textAlign: 'center',
    margin: 0,
    marginTop: '0.5em',
    marginBottom: '1em',
  };

  const explainer: IStyle = {
    width: '100%',
    margin: 0,
    marginTop: '2rem',
    textAlign: 'center',
    lineHeight: theme.fonts.large.lineHeight,
  };

  return mergeStyleSets({
    main,
    palette,
    title,
    explainer,
  });
}

const saveButtonStyles = {
  root: {
    fontSize: '1em',
    width: '100%',
    maxWidth: 400,
  },
  label: {
    fontWeight: 'bold',
  },
};

const subscriberId = 'Page.StormDoorColors';

export const PageStormDoorColors: React.FunctionComponent = () => {
  const projectConfig = useProjectConfiguration(subscriberId);
  const theme = getTheme();
  const classes = getClasses(theme);
  const { showToast } = useToast();
  const nav = useNavigator();
  const [viewState, setViewState] = useState(BwjViewState.Ready);
  const [colorPrefs, setColorPrefs] = useState<StormDoorColor[]>([]);

  const onSavePreferences = () => {
    setViewState(BwjViewState.Loading);

    const request: IUpdateStormDoorColorPreferenceRequest = {
      colorPreferences: colorPrefs,
    };

    ColorApiClient.setStormDoorColorPreferences(request)
    .then(() => {
      const pc = {...projectConfig};
      pc.stormDoorColorPreferences = colorPrefs;
      CustomerAccountService.updateProjectConfiguration(pc);

      const url = getNextShoppingUrl(pc);
      nav.goTo(url);
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      showToast(MessageBarType.error, 'Unexpected error; please try again');
    });
  }

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: 'Getting your catalog ready...',
      }}
    >
      <BwjPageContent
        contentDescription='primary content (no subcontent)'
        backgroundColor={BwjPalette.accentPrimary}
        style={{width: '100%', boxSizing: 'border-box', }}
      >
        <div className={classes.main}>
          <Icon iconName='Color' className={classes.palette} aria-hidden />
          <h2 className={classes.title}>{'Choose Preferred Door Colors'}</h2>
          <StormDoorColorSelector
            swatchSize={6.5}
            options={projectConfig.stormDoorColorOptions}
            selected={colorPrefs}
            showLabels={true}
            onSelectionChanged={setColorPrefs}
            rootStyle={{ margin: 0, marginBottom: '2rem', color: 'white', justifyContent: 'center', }}
          />
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            styles={saveButtonStyles}
            text={'Save preferences'}
            disabled={colorPrefs.length === 0}
            onClick={onSavePreferences}
          />
          <p className={classes.explainer}>
            <strong>No pressure</strong> &ndash; you can change these at any time.  This is just to help us get started.
          </p>
        </div>
      </BwjPageContent>
    </BwjViewManager>
  );
};